// The rating of the assessment
export interface Rating {
  // The label of the rating
  label: string;
  // The description of the rating
  description: string;
  // The value of the rating
  value: number;
}

// The ratings of the assessment
export interface AssessmentRatings {
  // The ratings for the knowledge category
  knowledge: Rating[];
  // The ratings for the process category
  process: Rating[];
  // The ratings for the metrics category
  metrics: Rating[];
  // The ratings for the adoption category
  adoption: Rating[];
  // The ratings for the automation category
  automation: Rating[];
}

// The help content of the assessment
export interface HelpContent {
  // The overview of the help content
  overview: string;
  // The details of the help content
  details: string;
  // The maturity levels of the help content
  maturity: {
    // The crawl maturity level of the help content
    crawl: string[];
    // The walk maturity level of the help content
    walk: string[];
    // The run maturity level of the help content
    run: string[];
  };
}

// The question of the assessment
export interface QuestionData {
  // The UUID of the question
  id: string;
  // The question of the question
  question: string;
  // The audiences of the question
  audiences: string[];
  // The help content of the question
  help: HelpContent;
  // The ratings of the question
  ratings: {
    // The ratings for the knowledge category
    knowledge: Rating[];
    // The ratings for the process category
    process: Rating[];
    // The ratings for the metrics category
    metrics: Rating[];
    // The ratings for the adoption category
    adoption: Rating[];
    // The ratings for the automation category
    automation: Rating[];
  };
  domain: {
    name: string;
    id: string;
  };
  capability: {
    name: string;
    id: string;
  };
}

// The capability of the assessment
export interface Capability {
  // The UUID of the capability
  id: string;
  // The name of the capability
  name: string;
  // The description of the capability
  description: string;
  // The documentation link of the capability
  documentation_link: string;
  // The questions of the capability
  questions: QuestionData[];
}

// The domain of the assessment
export interface Domain {
  // The UUID of the domain
  id: string;
  // The name of the domain
  name: string;
  // The description of the domain
  description: string;
  // The documentation link of the domain
  documentation_link: string;
  // The capabilities of the domain
  capabilities: Capability[];
  // The icon of the domain
  icon: string;
}

// The data of the assessment
export interface AssessmentData {
  // The id of the assessment
  id: string;
  // The name of the assessment
  name: string;
  // The description of the assessment
  description: string;
  // The ratings of the assessment
  ratings: AssessmentRatings;
  // The domains of the assessment
  domains: Domain[];
  // The audiences of the assessment
  audiences: string[];
  // The welcome message of the assessment
  welcome_message: string;
}

// Read optional API override from environment.
// This is intended for local demos or development where nginx is not used.
const apiUrlOverrideRaw = process.env.REACT_APP_API_URL?.trim();

// Treat "/api" (or empty) as "no override" so container behaviour is preserved.
// In containers, the UI must call "/api" exactly and let nginx rewrite it.
const apiUrlOverride =
  apiUrlOverrideRaw && apiUrlOverrideRaw !== '/api' ? apiUrlOverrideRaw : '';

// API metadata used only when calling the backend directly (no nginx).
// In container/prod deployments, these values are injected into nginx instead.
const API_CONFIG = {
  version: (process.env.REACT_APP_API_VERSION || 'v1').trim(),
  assessmentId: (process.env.REACT_APP_ASSESSMENT_ID || 'finops-maturity-v1').trim(),
};

// Final URL used to load the assessment JSON:
//
// - If an override is set:
//     Call the backend directly:
//     {REACT_APP_API_URL}/{version}/assessments/{assessmentId}
//
// - If no override is set:
//     Call "/api" exactly and rely on nginx at runtime to rewrite and proxy
//     the request using container environment variables.
const QUESTIONS_URL = apiUrlOverride
  ? `${apiUrlOverride.replace(/\/$/, '')}/${API_CONFIG.version}/assessments/${API_CONFIG.assessmentId}`
  : '/api';

// Load the assessment definition (domains, capabilities, questions, ratings).
export async function loadQuestions(): Promise<AssessmentData> {
  try {
    const response = await fetch(QUESTIONS_URL);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} url=${QUESTIONS_URL}`);
    }
    return (await response.json()) as AssessmentData;
  } catch (error) {
    console.error('Failed to load questions:', error);
    throw error;
  }
}


export function getHelpContent(question: QuestionData): HelpContent | undefined {
  return question.help;
}
